import React from 'react';
import { motion } from 'framer-motion';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BoltIcon from '@mui/icons-material/Bolt';
import LanguageIcon from '@mui/icons-material/Language';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useIsMobile } from '../hooks/useMediaQuery';
type Intensity = 'full' | 'soft' | 'light';
interface AnimatedBackgroundProps {
  /** 'full' hero, 'soft' secciones azules, 'light' secciones claras. */
  intensity?: Intensity;
  /** Barrido de luz una sola vez al cargar. */
  sweep?: boolean;
  /** Iconos flotando detrás del contenido. */
  icons?: boolean;
}
const particles = [{
  top: '18%',
  left: '12%',
  size: 6,
  delay: 0,
  duration: 7
}, {
  top: '32%',
  left: '78%',
  size: 4,
  delay: 1.2,
  duration: 9
}, {
  top: '62%',
  left: '22%',
  size: 5,
  delay: 2.4,
  duration: 8
}, {
  top: '74%',
  left: '64%',
  size: 3,
  delay: 0.8,
  duration: 10
}, {
  top: '46%',
  left: '48%',
  size: 4,
  delay: 3.1,
  duration: 11
}, {
  top: '86%',
  left: '88%',
  size: 5,
  delay: 1.9,
  duration: 9
}];
const floatingIcons = [{
  Icon: RocketLaunchIcon,
  top: '10%',
  left: '6%',
  size: 34,
  delay: 0,
  duration: 7
}, {
  Icon: TrendingUpIcon,
  top: '16%',
  left: '52%',
  size: 30,
  delay: 1.4,
  duration: 9
}, {
  Icon: BoltIcon,
  top: '48%',
  left: '4%',
  size: 28,
  delay: 2.2,
  duration: 6
}, {
  Icon: LanguageIcon,
  top: '12%',
  left: '86%',
  size: 32,
  delay: 0.6,
  duration: 8
}, {
  Icon: ChatBubbleOutlineIcon,
  top: '78%',
  left: '40%',
  size: 26,
  delay: 3,
  duration: 7.5
}, {
  Icon: ScheduleIcon,
  top: '30%',
  left: '34%',
  size: 24,
  delay: 1.8,
  duration: 10
}, {
  Icon: BarChartIcon,
  top: '84%',
  left: '16%',
  size: 26,
  delay: 2.6,
  duration: 8.5
}, {
  Icon: AutoAwesomeIcon,
  top: '56%',
  left: '90%',
  size: 28,
  delay: 0.4,
  duration: 9.5
}, {
  Icon: LockOutlinedIcon,
  top: '68%',
  left: '74%',
  size: 24,
  delay: 3.4,
  duration: 7
}, {
  Icon: SearchIcon,
  top: '40%',
  left: '66%',
  size: 26,
  delay: 2,
  duration: 11
}];
export function AnimatedBackground({
  intensity = 'full',
  sweep = false,
  icons = false
}: AnimatedBackgroundProps) {
  const light = intensity === 'light';
  const soft = intensity === 'soft';
  const isMobile = useIsMobile();
  const lineColor = light ? 'rgba(27,77,204,0.35)' : 'rgba(255,255,255,0.55)';
  // En móvil se recortan las capas decorativas más caras de pintar.
  const visibleIcons = isMobile ? floatingIcons.slice(0, 4) : floatingIcons;
  const visibleParticles = isMobile ? particles.slice(0, 3) : particles;
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-12" data-mp="3f4ycvhdnyigy">
      {/* Rejilla que se desplaza lentamente */}
      <div className={`absolute -inset-20 animate-grid-pan [background-size:64px_64px] ${light ? 'opacity-[0.05]' : soft ? 'opacity-[0.05]' : 'opacity-[0.08]'}`} style={{
      backgroundImage: `linear-gradient(${lineColor} 1px, transparent 1px), linear-gradient(90deg, ${lineColor} 1px, transparent 1px)`
    }} data-id="element-13" data-mp="30ihjmol14ccs" />

      {/* Auroras que flotan */}
      <div className={`absolute -top-40 right-[-12%] h-[620px] w-[620px] animate-drift-a rounded-full will-change-transform ${light ? 'bg-[radial-gradient(circle,rgba(255,213,0,0.22),transparent_65%)]' : 'bg-[radial-gradient(circle,rgba(255,213,0,0.18),transparent_65%)]'} ${soft ? 'opacity-60' : ''}`} data-id="element-14" data-mp="3jfae2o5xnj8p" />
      <div className={`absolute -bottom-40 -left-32 h-[560px] w-[560px] animate-drift-b rounded-full will-change-transform ${light ? 'bg-[radial-gradient(circle,rgba(27,77,204,0.16),transparent_65%)]' : 'bg-[radial-gradient(circle,rgba(56,120,255,0.5),transparent_65%)]'} ${soft ? 'opacity-50' : ''}`} data-id="element-15" data-mp="61fgftxqkiit" />
      <div className={`absolute left-1/3 top-1/4 h-[420px] w-[420px] animate-drift-c rounded-full will-change-transform ${light ? 'bg-[radial-gradient(circle,rgba(34,197,94,0.12),transparent_70%)]' : 'bg-[radial-gradient(circle,rgba(27,77,204,0.55),transparent_70%)]'} ${soft ? 'opacity-40' : ''}`} data-id="element-16" data-mp="p4hgemwc5mpi" />

      {/* Haz de luz giratorio */}
      {!light && !isMobile ? <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" data-id="element-17" data-mp="68zqwqgm5tma">
          <div className={`h-[1400px] w-[1400px] animate-spin-slow [background:conic-gradient(from_0deg,transparent_0deg,rgba(255,255,255,0.45)_16deg,transparent_34deg,transparent_170deg,rgba(255,255,255,0.3)_192deg,transparent_212deg)] ${soft ? 'opacity-[0.04]' : 'opacity-[0.07]'}`} data-id="element-18" data-mp="b8cghiyvdesk" />
        </div> : null}

      {/* Iconos flotando detrás del contenido */}
      {icons ? visibleIcons.map(({
      Icon,
      top,
      left,
      size,
      delay,
      duration
    }) => <span key={`${top}-${left}`} className={`absolute animate-float will-change-transform ${light ? 'text-brand-blue/[0.07]' : 'text-white/[0.12]'}`} style={{
      top,
      left,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`
    }} data-id="element-19" data-mp="375tiyl8mmnd4">
              <Icon sx={{
        fontSize: size
      }} data-id="element-20" data-mp="2jdmojrmgoqgc" />
            </span>) : null}

      {/* Partículas */}
      {visibleParticles.map(particle => <span key={`${particle.top}-${particle.left}`} className={`absolute animate-twinkle rounded-full will-change-transform ${light ? 'bg-brand-blue/40' : 'bg-brand-yellow/70'}`} style={{
      top: particle.top,
      left: particle.left,
      width: particle.size,
      height: particle.size,
      animationDelay: `${particle.delay}s`,
      animationDuration: `${particle.duration}s`
    }} data-id="element-21" data-mp="2egmjnk1cltqt" />)}

      {/* Barrido de luz al cargar */}
      {sweep ? <motion.div initial={{
      x: '-40%',
      opacity: 0
    }} animate={{
      x: '140%',
      opacity: [0, 0.45, 0]
    }} transition={{
      duration: 2,
      delay: 0.2,
      ease: 'easeInOut'
    }} className="absolute inset-y-0 left-0 w-1/3 -skew-x-12 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.16),transparent)]" data-id="element-22" data-mp="225lri2fxgtm6" /> : null}
    </div>;
}